export enum ACHIEVEMENT_TIERS {
  NEUTRAL = 'NEUTRAL',
  BRONZE = 'BRONZE',
  SILVER = 'SILVER',
  GOLD = 'GOLD',

  QUEST = 'QUEST',
}

export enum ACHIEVEMENT_MODES {
  BADGE = 'BADGE',
  QUEST = 'QUEST',
}

export enum ACHIEVEMENT_CATEGORIES {
  ATTENDANCE = 'ATTENDANCE',
  DISCOVERY = 'DISCOVERY',
  SOCIAL = 'SOCIAL',
}

type AchievementTierSettings = {
  threshold?: number;
  isListedIfNotOwned?: boolean;
};

export enum ACHIEVEMENT_TYPES {
  CONNECTION_STREAK = 'CONNECTION_STREAK',
  EASTER_EGGS_2025 = 'EASTER_EGGS_2025',
  EXERCISE_COMPLETED = 'EXERCISE_COMPLETED',
  EXERCISE_PERFECT_SCORE = 'EXERCISE_PERFECT_SCORE',
  FEEDBACK_CAMPAIGN_ANSWERED = 'FEEDBACK_CAMPAIGN_ANSWERED',
  HELP_IT_TEAM = 'HELP_IT_TEAM',
  LESSON_AUDIO_LISTENED = 'LESSON_AUDIO_LISTENED',
  LESSON_COMPLETED = 'LESSON_COMPLETED',
  APPOINTMENT_SCHEDULED = 'APPOINTMENT_SCHEDULED',
  PROFILE_PICTURE_ADDED = 'PROFILE_PICTURE_ADDED',
  VIRTUAL_CLASSROOM_ATTENDED = 'VIRTUAL_CLASSROOM_ATTENDED',
  CLASS_JOINED = 'CLASS_JOINED',

  // Quests
  CAMPUS_EXPLORER = 'CAMPUS_EXPLORER',
  GOOD_HABITS = 'GOOD_HABITS',
  GAIN_CONFIDENCE = 'GAIN_CONFIDENCE',
  SNAP_FINGERS = 'SNAP_FINGERS',
}

export enum ACHIEVEMENT_FALLBACK_TYPES {
  // Defaults for unlisted achievements
  DEFAULT = 'DEFAULT',
  DEFAULT_MULTIPLE = 'DEFAULT_MULTIPLE',
}

export const ACHIEVEMENT_CATEGORIES_MAP: Record<
  Exclude<ACHIEVEMENT_TYPES, 'DEFAULT' | 'DEFAULT_MULTIPLE'>,
  ACHIEVEMENT_CATEGORIES
> = {
  // Achievements
  [ACHIEVEMENT_TYPES.CONNECTION_STREAK]: ACHIEVEMENT_CATEGORIES.ATTENDANCE,
  [ACHIEVEMENT_TYPES.EASTER_EGGS_2025]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.EXERCISE_COMPLETED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.HELP_IT_TEAM]: ACHIEVEMENT_CATEGORIES.SOCIAL,
  [ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.LESSON_COMPLETED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.CLASS_JOINED]: ACHIEVEMENT_CATEGORIES.SOCIAL,

  // Quests
  [ACHIEVEMENT_TYPES.CAMPUS_EXPLORER]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.GOOD_HABITS]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.GAIN_CONFIDENCE]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
  [ACHIEVEMENT_TYPES.SNAP_FINGERS]: ACHIEVEMENT_CATEGORIES.DISCOVERY,
};

export const ACHIEVEMENTS_DATA: Record<
  ACHIEVEMENT_TYPES | ACHIEVEMENT_FALLBACK_TYPES,
  Partial<Record<ACHIEVEMENT_TIERS, AchievementTierSettings>>
> = {
  // Achievements
  [ACHIEVEMENT_TYPES.CONNECTION_STREAK]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 5,
    },

    [ACHIEVEMENT_TIERS.BRONZE]: {
      threshold: 15,
    },

    [ACHIEVEMENT_TIERS.SILVER]: {
      threshold: 30,
    },

    [ACHIEVEMENT_TIERS.GOLD]: {
      threshold: 100,
    },
  },

  [ACHIEVEMENT_TYPES.EASTER_EGGS_2025]: {
    [ACHIEVEMENT_TIERS.GOLD]: {
      isListedIfNotOwned: false,
    },
  },

  [ACHIEVEMENT_TYPES.EXERCISE_COMPLETED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE]: {
    [ACHIEVEMENT_TIERS.GOLD]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.HELP_IT_TEAM]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
      isListedIfNotOwned: false,
    },
  },

  [ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.LESSON_COMPLETED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.CLASS_JOINED]: {
    [ACHIEVEMENT_TIERS.NEUTRAL]: {
      threshold: 1,
    },
  },

  // Quests
  [ACHIEVEMENT_TYPES.CAMPUS_EXPLORER]: {
    [ACHIEVEMENT_TIERS.QUEST]: {
      threshold: 1,
    },
  },

  [ACHIEVEMENT_TYPES.GOOD_HABITS]: {
    [ACHIEVEMENT_TIERS.QUEST]: {
      threshold: 1,
      isListedIfNotOwned: false,
    },
  },

  [ACHIEVEMENT_TYPES.GAIN_CONFIDENCE]: {
    [ACHIEVEMENT_TIERS.QUEST]: {
      threshold: 1,
      isListedIfNotOwned: false,
    },
  },

  [ACHIEVEMENT_TYPES.SNAP_FINGERS]: {
    [ACHIEVEMENT_TIERS.QUEST]: {
      threshold: 1,
      isListedIfNotOwned: false,
    },
  },

  // Defaults for unlisted achievements
  [ACHIEVEMENT_FALLBACK_TYPES.DEFAULT]: {
    [ACHIEVEMENT_TIERS.GOLD]: {
      isListedIfNotOwned: false,
    },
  },

  [ACHIEVEMENT_FALLBACK_TYPES.DEFAULT_MULTIPLE]: {
    [ACHIEVEMENT_TIERS.GOLD]: {
      isListedIfNotOwned: false,
    },
  },
};
